import type { BulkLookupMatch, BulkLookupResult, TenantAccess } from '@konyaxl/shared';
import { BULK_LOOKUP_MAX_CODES, parseBarcodeList } from '@konyaxl/shared';
import { type ReactNode, useMemo, useState } from 'react';
import { QrDialog } from '../components/QrDialog.js';
import { ApiRequestError, api, downloadFile } from '../core/api.js';
import { panoyaKopyala } from '../core/pano.js';

interface Props {
  tenant: TenantAccess;
  onBack: () => void;
  /** Üstte gösterilecek sekme şeridi; tek başına açıldığında verilmez. */
  sekmeler?: ReactNode;
}

type Sekme = 'eslesen' | 'bulunamayan';

/** Panoya tek satır hâlinde gider; ayırıcı sade virgül. */
const AYRAC = ',';

/**
 * Toplu gönderi sorgulama — bilgisayarda yapılan iş.
 *
 * Elindeki gönderi listesini yapıştırıp tek seferde "bunlardan hangileri
 * sisteme girilmiş" sorusunu sorar. Sonuç iki sekmeye ayrılır çünkü iki liste
 * iki ayrı işe yarıyor: eşleşenler kontrol için, bulunamayanlar takip için.
 * Tek listede "durum" sütunuyla vermek, kullanıcıyı her seferinde süzmeye
 * zorlardı.
 */
export function BulkQueryPage({ tenant, onBack, sekmeler }: Props) {
  const [metin, setMetin] = useState('');
  const [sonuc, setSonuc] = useState<BulkLookupResult | null>(null);
  /**
   * Sonucu üreten metin ayrı tutuluyor: kullanıcı sorgudan sonra kutuyu
   * değiştirebiliyor ve Excel'in ekrandakiyle aynı listeyi vermesi gerekiyor.
   */
  const [sorgulanan, setSorgulanan] = useState('');
  const [sekme, setSekme] = useState<Sekme>('eslesen');
  const [mesgul, setMesgul] = useState(false);
  const [indiriliyor, setIndiriliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [not, setNot] = useState<string | null>(null);
  const [seciliKayit, setSeciliKayit] = useState<BulkLookupMatch | null>(null);
  const [seciliKod, setSeciliKod] = useState<string | null>(null);

  /** Sunucuyla aynı ayrıştırıcı — ekrandaki sayı sorgulanacak kod sayısıdır. */
  const onizleme = useMemo(() => parseBarcodeList(metin), [metin]);

  async function sorgula() {
    if (onizleme.length === 0) return;
    setMesgul(true);
    setHata(null);
    setNot(null);

    try {
      const res = await api.post<BulkLookupResult>(`/${tenant.slug}/lookup`, {
        text: metin,
      });
      setSonuc(res);
      setSorgulanan(metin);
      // Boş sekmeyle karşılaşmasın: hangi listede sonuç varsa oraya geç.
      setSekme(res.matched.length > 0 ? 'eslesen' : 'bulunamayan');
    } catch (err) {
      setHata(err instanceof ApiRequestError ? err.message : 'Sorgu yapılamadı');
    } finally {
      setMesgul(false);
    }
  }

  const aktifKodlar =
    sekme === 'eslesen'
      ? (sonuc?.matched.map((m) => m.code) ?? [])
      : (sonuc?.missing ?? []);

  async function kodlariKopyala() {
    if (aktifKodlar.length === 0) return;

    const basarili = await panoyaKopyala(aktifKodlar.join(AYRAC));
    setNot(
      basarili
        ? `${aktifKodlar.length} barkod panoya kopyalandı`
        : 'Panoya kopyalanamadı',
    );
    setTimeout(() => setNot(null), 2500);
  }

  async function excelIndir() {
    if (!sonuc) return;
    setIndiriliyor(true);
    setHata(null);
    try {
      await downloadFile(
        `/${tenant.slug}/lookup/export.xlsx`,
        `${tenant.slug}-toplu-sorgu.xlsx`,
        { text: sorgulanan },
      );
    } catch {
      setHata('Excel dosyası indirilemedi');
    } finally {
      setIndiriliyor(false);
    }
  }

  function yeniSorgu() {
    setSonuc(null);
    setSorgulanan('');
    setMetin('');
    setHata(null);
    setNot(null);
  }

  return (
    <>
      <header className="ust-bar">
        <button className="btn-geri" onClick={onBack} aria-label="Geri">
          ‹
        </button>
        <h1>{tenant.name} — Toplu gönderi sorgula</h1>
        <div className="ust-bar-sag">
          {sonuc && (
            <>
              <button className="btn-excel" onClick={yeniSorgu} disabled={mesgul}>
                Yeni sorgu
              </button>
              <button
                className="btn-excel"
                onClick={() => void excelIndir()}
                disabled={indiriliyor}
              >
                {indiriliyor ? 'Hazırlanıyor…' : 'Excel indir'}
              </button>
            </>
          )}
        </div>
      </header>

      {sekmeler}

      <main className="icerik">
        {hata && <div className="hata-kutu">{hata}</div>}
        {not && <div className="basari-kutu">{not}</div>}

        {!sonuc && (
          <section className="bolum">
            <h2 className="bolum-baslik">Gönderi listesi</h2>
            <p className="bolum-aciklama">
              Gönderi barkodlarını alt alta yapıştırın. Excel'den kopyalanan
              sütunlar da çalışır — satır, virgül, noktalı virgül ve sekme
              ayırıcı kabul edilir. Tek seferde en fazla{' '}
              {BULK_LOOKUP_MAX_CODES.toLocaleString('tr-TR')} kod sorgulanır.
            </p>

            <textarea
              className="liste-alan"
              value={metin}
              onChange={(e) => setMetin(e.target.value)}
              placeholder={'6901234567890\n6901234567891\n6901234567892'}
              rows={12}
              spellCheck={false}
              autoFocus
            />

            <div className="liste-arac">
              <span className="onizleme-bilgi">
                {onizleme.length > 0
                  ? `${onizleme.length} kod algılandı`
                  : 'Henüz kod girilmedi'}
              </span>

              <button
                className="btn-birincil dar"
                onClick={() => void sorgula()}
                disabled={mesgul || onizleme.length === 0}
              >
                {mesgul ? 'Sorgulanıyor…' : 'Sorgula'}
              </button>
            </div>
          </section>
        )}

        {sonuc && (
          <>
            <div className="ozet-kartlar">
              <div className="ozet-kart">
                <span className="ozet-sayi">{sonuc.total}</span>
                <span className="ozet-etiket">Sorgulanan</span>
              </div>
              <div className="ozet-kart yesil">
                <span className="ozet-sayi">{sonuc.matched.length}</span>
                <span className="ozet-etiket">Eşleşen</span>
              </div>
              <div className="ozet-kart turuncu">
                <span className="ozet-sayi">{sonuc.missing.length}</span>
                <span className="ozet-etiket">Kayıt bulunamayan</span>
              </div>
            </div>

            {sonuc.skipped > 0 && (
              <div className="hata-kutu">
                Listede {sonuc.total + sonuc.skipped} kod vardı; ilk{' '}
                {sonuc.total.toLocaleString('tr-TR')} tanesi sorgulandı,{' '}
                {sonuc.skipped.toLocaleString('tr-TR')} kod sorgulanmadı. Kalanı
                ayrı bir sorguda çalıştırın.
              </div>
            )}

            <div className="sorgu-sekmeler">
              <button
                className={sekme === 'eslesen' ? 'aktif' : ''}
                onClick={() => setSekme('eslesen')}
              >
                Eşleşen gönderiler ({sonuc.matched.length})
              </button>
              <button
                className={sekme === 'bulunamayan' ? 'aktif' : ''}
                onClick={() => setSekme('bulunamayan')}
              >
                Kayıt bulunamayan ({sonuc.missing.length})
              </button>
            </div>

            <div className="liste-arac">
              <span className="onizleme-bilgi">
                {aktifKodlar.length} barkod
              </span>
              <button
                className="btn-birincil dar"
                onClick={() => void kodlariKopyala()}
                disabled={aktifKodlar.length === 0}
              >
                Barkodları kopyala
              </button>
            </div>

            {aktifKodlar.length === 0 ? (
              <div className="bos-durum">
                {sekme === 'eslesen'
                  ? 'Listedeki kodların hiçbiri sistemde kayıtlı değil'
                  : 'Listedeki kodların tamamı sistemde kayıtlı'}
              </div>
            ) : sekme === 'eslesen' ? (
              <table className="kayit-tablo">
                <thead>
                  <tr>
                    <th>Gönderi barkodu</th>
                    {/* Sabit "Ürün Kodu" yazılmıyor: yapıştırılan kod ürün
                        kodu tarafına da düşebiliyor, o durumda bu sütunda
                        barkod görünür ve başlık yanlış olurdu. */}
                    <th>Eşleştiği kod</th>
                    <th>Tarih</th>
                    <th>Saat</th>
                    <th>Okutan</th>
                  </tr>
                </thead>
                <tbody>
                  {sonuc.matched.map((m) => {
                    const d = new Date(m.createdAt);
                    return (
                      <tr
                        key={m.code}
                        className="tiklanabilir"
                        onClick={() => setSeciliKayit(m)}
                        title="Kodları kopyalamak ve QR görmek için tıklayın"
                      >
                        <td className="kod">{m.code}</td>
                        <td className="kod">
                          {/* Sorgulanan kod ürün kodu tarafına düşmüşse
                              eşleştiği barkod gösterilir. */}
                          {m.code === m.codeA ? m.codeB : m.codeA}
                        </td>
                        <td>{d.toLocaleDateString('tr-TR')}</td>
                        <td>{d.toLocaleTimeString('tr-TR', { hour12: false })}</td>
                        <td>{m.createdByName}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="kalan-izgara">
                {sonuc.missing.map((kod) => (
                  <button
                    key={kod}
                    className="kalan-etiket"
                    onClick={() => setSeciliKod(kod)}
                  >
                    {kod}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {seciliKayit && (
        <QrDialog
          kodlar={[
            { etiket: tenant.scanRules.a.label, deger: seciliKayit.codeA ?? '' },
            { etiket: tenant.scanRules.b.label, deger: seciliKayit.codeB ?? '' },
          ].filter((k) => k.deger)}
          altNot={tenant.name}
          onClose={() => setSeciliKayit(null)}
        />
      )}

      {seciliKod && (
        <QrDialog
          kodlar={[{ etiket: 'Gönderi barkodu', deger: seciliKod }]}
          altNot={tenant.name}
          onClose={() => setSeciliKod(null)}
        />
      )}
    </>
  );
}
